import type { AriaAttributes } from 'react';

import styles from './ActivityIndicator.module.css';

import { PawLoader } from './components';

/** @deprecated Replaced by {@link ActivityIndicatorColor}. */
export type ActivityIndicatorMode = 'light' | 'dark';

export type ActivityIndicatorColor =
  | 'white'
  | 'carrot'
  | 'kale'
  | 'kale-dark'
  | 'error-red-dark';
export type ActivityIndicatorRole = 'status' | 'presentation';

export interface ActivityIndicatorProps {
  /**
   * Use `dark` when placing the indicator on lighter backgrounds.
   *
   * @deprecated Set {@link color} to `white` or `carrot`.
   */
  mode?: ActivityIndicatorMode;
  /**
   * Set the color of the indicator. Use `white` when placing the indicator on
   * darker backgrounds.
   *
   * @defaultValue white
   */
  color?: ActivityIndicatorColor;
  /**
   * The role attribute of the indicator. Toggle this to `presentation` if the
   * parent is handling the aria-live attribute.
   *
   * @defaultValue `status`
   */
  role?: ActivityIndicatorRole;
  /**
   * The aria-label attribute of the indicator.
   *
   * @defaultValue 'Loading…' when {@link role} is `status`, otherwise `undefined`.
   */
  ['aria-label']?: AriaAttributes['aria-label'];
}

/**
 * Render an activity indicator with `role="status"` to inform the user a task
 * is in progress.
 *
 * @see https://corgi-x.tfd.engineering/components/activityindicator
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/status_role
 */
export function ActivityIndicator(props: ActivityIndicatorProps) {
  const { role = 'status' } = props;

  const defaultColor = props.mode === 'dark' ? 'carrot' : 'white';
  const defaultLabel = role === 'presentation' ? '' : 'Loading…'; // Presentation role doesn't need an aria-label

  const { color = defaultColor, 'aria-label': ariaLabel = defaultLabel } =
    props;
  const className = `${styles.container} ${styles[color]}`;
  return (
    <span role={role} aria-label={ariaLabel} className={className}>
      <span className={styles.dots}>
        <span data-activity-dot />
        <span data-activity-dot />
        <span data-activity-dot />
      </span>
      <span className={styles.pawLoader}>
        <PawLoader />
      </span>
    </span>
  );
}
